import * as RadixPopover from '@radix-ui/react-popover';
import cn from 'classnames';
import { FC, ReactNode, useState } from 'react';

import { Icon, type IconNames } from '@/shared/ui';

import { getPopoverCollisionPadding, WINDOW_EDGE_GAP } from '../lib';

import styles from './SettingsPopover.module.scss';

type Props = {
  label: string;
  children: ReactNode;
  className?: string;
  icon?: IconNames;
  iconSize?: number;
  // The trigger shows a value behind it, e.g. links or guidance.
  marked?: boolean;
  // A number shown next to the icon, e.g. how many links a line has.
  count?: number;
  // Shown instead of the icon where the trigger names what it opens.
  text?: ReactNode;
  disabled?: boolean;
  open?: boolean;
  // Where the popover opens; under its trigger by default.
  side?: 'top' | 'bottom';
  // Rendered over the whole page, the sticky toolbar and the app header
  // among it, instead of inside the editor.
  portal?: boolean;
  dataComponent?: string;
  onOpenChange?: (open: boolean) => void;
  // Where the focus goes once the popover closes; by default back to its
  // trigger.
  onCloseAutoFocus?: (event: Event) => void;
};

// Settings of a section, field, line or block that have no place in its row.
// The content stays inside the editor unless `portal`, so a disabled editor
// disables its inputs too.
export const SettingsPopover: FC<Props> = ({
  label,
  children,
  className,
  icon = 'dots',
  iconSize = 16,
  marked = false,
  count,
  text,
  disabled = false,
  open,
  side = 'bottom',
  portal = false,
  dataComponent,
  onOpenChange,
  onCloseAutoFocus,
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isOpen = open ?? uncontrolledOpen;

  const changeOpen = (next: boolean) => {
    setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  const content = (
    <RadixPopover.Content
      className={cn(styles.content, portal && styles.portaled)}
      aria-label={label}
      side={side}
      align="end"
      sideOffset={6}
      // Measured only while open: every block of a template has one.
      collisionPadding={isOpen ? getPopoverCollisionPadding() : WINDOW_EDGE_GAP}
      // Escape closes the popover only, never the modal or pop-up it sits
      // in, which listen on the window.
      onEscapeKeyDown={(event) => event.stopPropagation()}
      onCloseAutoFocus={onCloseAutoFocus}
    >
      {children}
    </RadixPopover.Content>
  );

  return (
    <RadixPopover.Root open={isOpen} onOpenChange={changeOpen}>
      <RadixPopover.Trigger asChild disabled={disabled}>
        <button
          type="button"
          className={cn(
            styles.trigger,
            marked && styles.marked,
            count !== undefined && styles.counted,
            text !== undefined && styles.texted,
            className,
          )}
          aria-label={label}
          title={label}
          data-component={dataComponent}
        >
          {text ?? <Icon name={icon} size={iconSize} />}
          {count !== undefined && <span className="p5">{count}</span>}
        </button>
      </RadixPopover.Trigger>
      {portal ? <RadixPopover.Portal>{content}</RadixPopover.Portal> : content}
    </RadixPopover.Root>
  );
};
